/**
 * `<caos-tooltip>` — a short label shown on hover or focus. Never carries an action.
 * (Catalog: Overlays › Tooltip — a short label shown on hover or focus.)
 *
 * The trigger is the default slot; the tip text is the `text` attribute (or a named
 * `tip` slot for richer content). The bubble reveals on hover AND on keyboard focus
 * of the trigger (`:focus-within`), so it is reachable without a pointer, and it
 * carries `--caos-shadow-float` because it floats. `placement` is the side of the
 * trigger it prefers: `top` (default) · `bottom` · `left` · `right`. Where that side
 * has no room and the opposite side does, it opens there instead, and it slides along
 * the side it opens on so the whole tip stays on screen (CAOS-659). The trigger wrapper
 * is focusable and points `aria-describedby` at the tip. Styling seams:
 * `::part(trigger)`, `::part(tip)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { placePanel, visibleViewport, type PanelSide } from '../anchored-panel.js';

export type CaosTooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

let uid = 0;

const sheet = makeSheet(`
  :host { display: inline-block; position: relative; vertical-align: middle; }

  .trigger { display: inline-flex; cursor: default; }
  .trigger:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); border-radius: var(--caos-radius-sm); }

  .tip {
    position: absolute;
    /* A NAMED layer, not a number — see the header note on layering (CAOS-586). Above dropdown
       AND overlay: a tooltip's trigger can live inside either. */
    z-index: var(--caos-layer-tooltip);
    max-width: 260px;
    width: max-content;
    padding: var(--caos-space-2) var(--caos-space-3);
    border-radius: var(--caos-radius-sm);
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface);
    color: var(--caos-color-text);
    box-shadow: var(--caos-shadow-float);
    font-family: var(--caos-font);
    font-size: 12px;
    line-height: 1.45;
    /* hidden until hover/focus; opacity + visibility so it is out of the a11y/paint tree */
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.12s ease;
    pointer-events: none;
  }
  .tip:empty { display: none; }

  /* placements */
  :host([placement="bottom"]) .tip { top: calc(100% + 6px); left: 50%; transform: translateX(-50%); }
  :host([placement="left"]) .tip   { right: calc(100% + 6px); top: 50%; transform: translateY(-50%); }
  :host([placement="right"]) .tip  { left: calc(100% + 6px); top: 50%; transform: translateY(-50%); }
  /* default = top */
  :host(:not([placement])) .tip,
  :host([placement="top"]) .tip { bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%); }

  :host(:hover) .tip,
  :host(:focus-within) .tip { opacity: 1; visibility: visible; }

  @media (prefers-reduced-motion: reduce) {
    .tip { transition: none; }
  }
`);

export class CaosTooltip extends CaosElement {
  static get observedAttributes(): string[] {
    return ['text'];
  }

  private trigger: HTMLSpanElement;
  private tip: HTMLDivElement;

  constructor() {
    super(sheet);
    const tipId = `caos-tip-${++uid}`;

    this.trigger = document.createElement('span');
    this.trigger.className = 'trigger';
    this.trigger.setAttribute('part', 'trigger');
    this.trigger.tabIndex = 0;
    this.trigger.setAttribute('aria-describedby', tipId);
    this.trigger.append(document.createElement('slot'));

    this.tip = document.createElement('div');
    this.tip.className = 'tip';
    this.tip.id = tipId;
    this.tip.setAttribute('part', 'tip');
    this.tip.setAttribute('role', 'tooltip');
    const tipSlot = document.createElement('slot');
    tipSlot.name = 'tip';
    this.tip.append(tipSlot);

    this.root.append(this.trigger, this.tip);

    // Escape dismisses (blurs the trigger so the tip hides).
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.trigger.blur();
    });

    // CAOS-659 — measured where it opens, the moment it opens, by pointer and by keyboard alike.
    this.addEventListener('mouseenter', () => this.place());
    this.addEventListener('focusin', () => this.place());
  }

  /**
   * Put the tip on the side `placement` asks for, unless that side has no room and the opposite
   * side does, then slide it along that side until it is on screen. Written as offsets from the
   * host, because the tip is positioned inside it; the stylesheet's placement rules are only where
   * it sits before this has run. `--caos-panel-pointer` says where along the tip the trigger's
   * centre falls, for a pointer drawn through `::part(tip)`.
   */
  private place(): void {
    const asked = this.getAttribute('placement');
    const side: PanelSide = asked === 'bottom' || asked === 'left' || asked === 'right' ? asked : 'top';
    const host = this.getBoundingClientRect();
    const box = this.tip.getBoundingClientRect();
    const panel = { width: box.width || this.tip.offsetWidth, height: box.height || this.tip.offsetHeight };
    const placed = placePanel({ anchor: host, panel, viewport: visibleViewport(), side, align: 'center', gap: 6 });
    // The placement is in screen pixels and the offsets are read in the host's own. Under a scaled
    // ancestor the two differ by that scale, which the host's drawn size over its laid-out size gives.
    const scaleX = this.offsetWidth > 0 && host.width > 0 ? host.width / this.offsetWidth : 1;
    const scaleY = this.offsetHeight > 0 && host.height > 0 ? host.height / this.offsetHeight : 1;
    const style = this.tip.style;
    style.left = `${Math.round((placed.left - host.left) / scaleX)}px`;
    style.top = `${Math.round((placed.top - host.top) / scaleY)}px`;
    style.right = 'auto';
    style.bottom = 'auto';
    style.transform = 'none';
    this.tip.dataset['side'] = placed.side;
    const along = placed.side === 'top' || placed.side === 'bottom' ? scaleX : scaleY;
    style.setProperty('--caos-panel-pointer', `${Math.round(placed.pointer / along)}px`);
  }

  connectedCallback(): void {
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  private sync(): void {
    const text = this.getAttribute('text');
    // `text` populates the tip only when no `tip`-slotted content is provided.
    const hasSlotted = this.querySelector('[slot="tip"]') !== null;
    // Keep any slotted <slot> node; only manage the text node we own.
    const existing = this.tip.querySelector('.tip-text');
    if (text && !hasSlotted) {
      if (existing) existing.textContent = text;
      else {
        const span = document.createElement('span');
        span.className = 'tip-text';
        span.textContent = text;
        this.tip.append(span);
      }
    } else if (existing) {
      existing.remove();
    }
  }
}

define('caos-tooltip', CaosTooltip);

declare global {
  interface HTMLElementTagNameMap {
    'caos-tooltip': CaosTooltip;
  }
}
